// Second-device detection (Deploy I) — runs on the candidate's own device. Only episode times leave the
// browser (no images). MediaPipe Tasks (self-hosted under /mediapipe): FaceLandmarker → face count + head
// pose; ObjectDetector (EfficientDet-Lite0, COCO) → "cell phone". Faces every ~2 s, phones every ~5 s,
// on a small hidden copy of the recording stream (no second camera open).
//
// Safety: everything is wrapped — any failure stops the analysis and reports analysis_degraded; the
// interview itself is never affected. Results live in plain JS (no React state) during the call.
// Budget: inference time is measured; slow machines back off. The first WARMUP_FRAMES inferences are not
// timed (GPU shader compilation makes them slow on every device — interview 61's "partly unavailable"),
// speed is judged on the average of the last AVG_FRAMES, and phone detection slows down (5 → 10 → 20 s)
// before it is switched off. analysis_degraded records the timings and GPU/CPU mode. (Main thread at low frequency: MediaPipe's WASM loader does not run in a module
// worker under our build — see the Deploy I notes.)
import { createEpisodeTracker } from './episodes';
import { pitchDownDegrees } from './headPose';

export const ASSET_BASE = '/mediapipe';
export const FACE_INTERVAL_MS = 2000;
export const PHONE_INTERVAL_MS = 5000;
export const MAX_FACE_INTERVAL_MS = 8000;
export const FACE_BUDGET_MS = 150;
export const PHONE_BUDGET_MS = 300;
export const MAX_PHONE_INTERVAL_MS = 20000;
export const WARMUP_FRAMES = 3;
export const AVG_FRAMES = 3;

let modelsPromise = null;

/** Load (once per page) — the lobby preloads so the interview starts without waiting. */
// Served from our own domain (copied at build time) and NOT bundled: webpack can't analyse the library's
// own dynamic imports, and under CI=true that warning fails the build.
export function loadModels(importer = () => import(/* webpackIgnore: true */ `${ASSET_BASE}/vision_bundle.mjs`)) {
  if (!modelsPromise) {
    modelsPromise = (async () => {
      const vision = await importer();
      const files = await vision.FilesetResolver.forVisionTasks(`${ASSET_BASE}/wasm`);
      const make = async (delegate) => ({
        face: await vision.FaceLandmarker.createFromOptions(files, {
          baseOptions: { modelAssetPath: `${ASSET_BASE}/models/face_landmarker.task`, delegate },
          runningMode: 'VIDEO', numFaces: 3, outputFacialTransformationMatrixes: true }),
        phone: await vision.ObjectDetector.createFromOptions(files, {
          baseOptions: { modelAssetPath: `${ASSET_BASE}/models/efficientdet_lite0.tflite`, delegate },
          runningMode: 'VIDEO', scoreThreshold: 0.4, categoryAllowlist: ['cell phone'], maxResults: 3 }),
      });
      try { return { ...(await make('GPU')), delegate: 'GPU' }; } catch (e) { return { ...(await make('CPU')), delegate: 'CPU' }; }
    })().catch((e) => { modelsPromise = null; throw e; });
  }
  return modelsPromise;
}

function hiddenVideo(stream) {
  const v = document.createElement('video');
  v.muted = true; v.playsInline = true; v.autoplay = true;
  v.width = 320; v.height = 240;
  v.srcObject = stream;
  v.play().catch(() => {});
  return v;
}

function brightness(video, canvas) {
  try {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, 16, 12);
    const d = ctx.getImageData(0, 0, 16, 12).data;
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    return sum / (d.length / 4);
  } catch (e) { return 128; }
}

export function facesFrom(result) {
  return (result?.faceLandmarks || []).map((pts, i) => {
    const xs = pts.map((p) => p.x); const ys = pts.map((p) => p.y);
    const minX = Math.min(...xs); const maxX = Math.max(...xs); const minY = Math.min(...ys); const maxY = Math.max(...ys);
    return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, w: maxX - minX, h: maxY - minY,
             matrix: result.facialTransformationMatrixes?.[i] };
  });
}

/** 5-s calibration on the setup page: median head pitch while looking at the screen (null if unusable). */
export async function calibrate(stream, { seconds = 5, now = () => performance.now() } = {}) {
  const models = await loadModels();
  const video = hiddenVideo(stream);
  const pitches = [];
  const end = Date.now() + seconds * 1000;
  while (Date.now() < end) {
    await new Promise((r) => setTimeout(r, 400));
    if (video.readyState < 2) continue;
    const faces = facesFrom(models.face.detectForVideo(video, now()));
    const p = faces.length ? pitchDownDegrees(faces[0].matrix) : null;
    if (p !== null) pitches.push(p);
  }
  video.srcObject = null;
  if (pitches.length < 5) return null;
  pitches.sort((a, b) => a - b);
  return pitches[Math.floor(pitches.length / 2)];
}

/**
 * Start analysing `stream`. send(type, detailsJson) records a signal. Returns { stop() } — stop() reports
 * every open episode. Never throws.
 */
export function startCameraAnalysis({ stream, send, baselinePitch = null, elapsedSeconds, now = () => performance.now() }) {
  let stopped = false;
  let timers = [];
  let faceInterval = FACE_INTERVAL_MS;
  let phoneInterval = PHONE_INTERVAL_MS;
  let phoneOn = true;
  let delegate = null;
  const faceTimes = [];
  const phoneTimes = [];
  let faceRuns = 0;
  let phoneRuns = 0;
  const avg = (xs) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null);
  const tracker = createEpisodeTracker({
    baselinePitch,
    onEpisode: (ep) => send(ep.type, JSON.stringify(ep)),
  });
  const degrade = (reason) => {
    try {
      send('analysis_degraded', JSON.stringify({
        reason, delegate, face_avg_ms: avg(faceTimes), phone_avg_ms: avg(phoneTimes),
        face_interval_ms: faceInterval, phone_interval_ms: phoneInterval, face_runs: faceRuns, phone_runs: phoneRuns,
      }));
    } catch (e) { /* ignore */ }
  };
  const video = hiddenVideo(stream);
  const canvas = document.createElement('canvas'); canvas.width = 16; canvas.height = 12;

  const schedule = (fn, ms) => { if (!stopped) timers.push(setTimeout(fn, ms)); };
  loadModels().then((models) => {
    delegate = models.delegate || null;
    const faceTick = () => {
      if (stopped) return;
      try {
        if (video.readyState >= 2) {
          const t0 = now();
          const res = models.face.detectForVideo(video, t0);
          const took = now() - t0;
          faceRuns += 1;
          if (faceRuns > WARMUP_FRAMES) { faceTimes.push(took); if (faceTimes.length > AVG_FRAMES) faceTimes.shift(); }
          if (faceTimes.length >= AVG_FRAMES && avg(faceTimes) > FACE_BUDGET_MS && faceInterval < MAX_FACE_INTERVAL_MS) {
            faceInterval = Math.min(MAX_FACE_INTERVAL_MS, faceInterval * 2);
            faceTimes.length = 0;                                     // judge the new interval afresh
          }
          const usable = brightness(video, canvas) > 25;            // dark frame → no conclusion
          tracker.face(elapsedSeconds(), facesFrom(res), usable);
        }
      } catch (e) { degrade(`face: ${e.message}`); stopped = true; return; }
      schedule(faceTick, faceInterval);
    };
    const phoneTick = () => {
      if (stopped || !phoneOn) return;
      try {
        if (video.readyState >= 2) {
          const t0 = now();
          const res = models.phone.detectForVideo(video, t0);
          const took = now() - t0;
          phoneRuns += 1;
          tracker.phone(elapsedSeconds(), (res?.detections || []).map((d) => ({ score: d.categories?.[0]?.score || 0 })));
          if (phoneRuns > WARMUP_FRAMES) { phoneTimes.push(took); if (phoneTimes.length > AVG_FRAMES) phoneTimes.shift(); }
          if (phoneTimes.length >= AVG_FRAMES && avg(phoneTimes) > PHONE_BUDGET_MS) {
            if (phoneInterval < MAX_PHONE_INTERVAL_MS) {               // slow device: check less often first
              phoneInterval = Math.min(MAX_PHONE_INTERVAL_MS, phoneInterval * 2);
              phoneTimes.length = 0;
            } else {
              phoneOn = false;
              degrade('phone detection too slow on this device — switched off');
              return;
            }
          }
        }
      } catch (e) { phoneOn = false; degrade(`phone: ${e.message}`); return; }
      schedule(phoneTick, phoneInterval);
    };
    schedule(faceTick, faceInterval);
    schedule(phoneTick, PHONE_INTERVAL_MS);
  }).catch((e) => degrade(`models did not load: ${e.message}`));

  let finished = false;
  return {
    stop() {
      if (finished) return;               // once; still flushes when analysis had stopped after an error
      finished = true;
      stopped = true;
      timers.forEach(clearTimeout); timers = [];
      try { tracker.flush(elapsedSeconds()); } catch (e) { /* ignore */ }
      video.srcObject = null;
    },
  };
}
